// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps, ButtonProps } from "@store/MobiusOverrides/Button/Props";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { buttonResetProps } from "@store/Themes/common/props/button";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { BreakpointValueProp } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import getArray from "@toolkit/Framework/Common/Utilities/getArray";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

export interface CarouselButtonPresentationProps extends ExpandStyles<CarouselButtonStyles> {
    iconMargin?: string;
    iconSize?: BreakpointValueProp<string>;
    iconSrc?: IconPresentationProps["src"];
    size?: BreakpointValueProp<string>;
}

export interface CarouselButtonProps extends CarouselButtonPresentationProps, HTMLAttributes<HTMLDivElement> {
    button?: ButtonProps;
    label?: string;
}

export interface CarouselButtonStyles {
    wrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const carouselButtonStyles: CarouselButtonStyles = {
    wrapper: {
        css: css`
            z-index: 1;
        `,
    },
    button: {
        ...buttonResetProps,
        css: css`
            ${buttonResetProps.css}
            display: flex;
            align-items: center;
            justify-content: center;
            width: 1em;
            height: 1em;
            border-radius: 50%;
            box-shadow: ${x => x.theme.shadows.e40};
            background-color: ${getColor("common.background")};
            /* Store Custom Code - Start */
            transition: ${transitionHoverStyles("color", "background-color")};

            &:not(:disabled) {
                color: ${getColor("primary")};

                &:hover {
                    color: ${getColor("primary.contrast")};
                    background-color: ${getColor("primary")};
                }
            }
            /* Store Custom Code - End */

            ${IconWrapper} {
                top: 0;
                margin: 0;
            }
        `,
    },
};

const CarouselButton: FC<CarouselButtonProps> = ({
    button,
    children,
    className,
    extendedStyles,
    iconMargin,
    iconSize = "0.53em",
    iconSrc,
    label,
    overridedStyles,
    size = pxToRem(32),
    ...htmlAttributes
}) => {
    const expandedStyles = useExpandProps(carouselButtonStyles, extendedStyles, overridedStyles);

    const styles = useMemo(() => {
        const _size = getArray(size);
        const _iconSize = getArray(iconSize);

        return mergeProps<CarouselButtonStyles>(expandedStyles, {
            button: {
                icon: {
                    src: iconSrc || "",
                    position: "left",
                    ...expandedStyles.button?.icon,
                },
                css: css`
                    ${breakpointValues(_size, "font-size")}

                    ${IconWrapper} {
                        ${breakpointValues(_iconSize?.map(x => (x !== null ? iconScalableStyles(x) : null)))}
                        ${cssProp("margin", iconMargin)}
                    }
                `,
            },
        });
    }, [expandedStyles, iconMargin, iconSrc, iconSize, size]);

    return (
        <StyledWrapper
            className={`carousel-button-wrapper${className ? ` ${className}` : ""}`}
            {...styles.wrapper}
            {...htmlAttributes}
        >
            <Button
                title={label}
                {...styles.button}
                {...button}
                className={`button${button?.className ? ` ${button.className}` : ""}`}
                tabIndex={htmlAttributes.tabIndex}
            >
                {label && <VisuallyHidden>{label}</VisuallyHidden>}
            </Button>
        </StyledWrapper>
    );
};

export default CarouselButton;
